import { Component, IEHandbookPreferences, IEMsnPreferences } from '@cms-max/medicare-api-sdk'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { Card, ICardProps } from 'components/Card/Card'
import useEmailTruncation from 'hooks/useEmailTruncation'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useTranslation } from 'react-i18next'

export type EmailAndDocumentSettingsProps = {
  elmoUnavailable?: boolean,
  // TODO: Make these required when we add the error scenario for these failing to load
  eHandbookPreferences?: IEHandbookPreferences,
  eMsnPreferences?: IEMsnPreferences,
  openModal: Function,
  serverError?: boolean,
  userEmail?: string
  /**
   * Default props that are passed ot the card components inside DocumentSettings
   */
  defaultCardProps?: Partial<ICardProps>,
}

export const EmailAndDocumentSettings = ({
  elmoUnavailable,
  eHandbookPreferences,
  eMsnPreferences,
  openModal,
  serverError,
  userEmail,
  defaultCardProps
}: EmailAndDocumentSettingsProps) => {
  const { t } = useTranslation('accountSettings')
  const flags = useFlags()

  const { original: emailAddress, truncated: truncatedEmailAddress } = useEmailTruncation(userEmail)

  let eMsnPreferencesDisplayText: string | undefined
  if (eMsnPreferences) {
    const { isEmailPreferred, customMediaDescription, media } = eMsnPreferences
    if (isEmailPreferred) {
      eMsnPreferencesDisplayText = t('eMsnPreferencesDisplayText.Electronic')
    } else if (media === 'Custom Format') {
      eMsnPreferencesDisplayText = customMediaDescription
    } else {
      eMsnPreferencesDisplayText = t(`eMsnPreferencesDisplayText.${media}`)
    }
  }

  let eHandbookPreferencesDisplayText: string | undefined
  if (eHandbookPreferences) {
    const { isEmailPreferred, media } = eHandbookPreferences
    eHandbookPreferencesDisplayText = isEmailPreferred ? t('eHandbookPreferencesDisplayText.Electronic') : t(`eHandbookPreferencesDisplayText.${media}`)
  }

  return (
    <div className='ds-l-row'>
      <div className='ds-l-md-col--12 ds-l-lg-col--4 '>
        <h2 className='ds-text-heading--xl ds-u-margin-bottom--2'>{t('paragraph.emailDocumentSettingsTitle')}</h2>
        <p>{t('paragraph.emailDocumentSettingsInfo')}</p>
      </div>
      <div className='ds-l-md-col--12 ds-l-lg-col--8'>
        {userEmail
          ? (
              <Card
                key="cardEmailAddress"
                dataTestId="cardEmailAddress"
                title={t('cards.emailAddress')}
                displayValue={emailAddress}
                // TODO - should there be minimal threshold for using truncated value?
                truncatedValue={truncatedEmailAddress}
                animationSubject={userEmail}
                buttonCallbackOrHref={() => openModal('acct-add-edit-email')}
                editButtonAriaLabel={t('btns.editEmailAddressAriaLabel')}
                buttonText={t('btns.edit')}
                buttonSiteActivityProps={{
                  core: {
                    component: Component.AccountSettings,
                    elementId: 'accountsettings_emailedit_click',
                    coreClickData: {
                      linkText: t('cards.emailAddress')
                    }
                  }
                }}
                {...defaultCardProps}
              />
            )
          : (
              <Card
                key="cardEmailAddress"
                dataTestId="cardEmailAddress"
                title={t('cards.emailAddress')}
                buttonCallbackOrHref={() => openModal('acct-add-edit-email')}
                editButtonAriaLabel={t('btns.addEmailAdressAriaLabel')}
                buttonText={t('btns.addEmailAddress')}
                buttonSiteActivityProps={{
                  core: {
                    component: Component.AccountSettings,
                    elementId: 'accountsettings_emailadd_click',
                    coreClickData: {
                      linkText: t('cards.emailAddress')
                    }
                  }
                }}
                animationSubject={userEmail}
                dropdown={{
                  contentId: 'dropdown-email-address',
                  content: (
                    <div>
                      <p className="my-u-m--0">{t('cards.dropdownEmail.para1')}</p>
                      <ul className='my-u-m--4'>
                        <li>{t('cards.dropdownEmail.li1')}</li>
                        <li>{t('cards.dropdownEmail.li2')}</li>
                      </ul>
                    </div>
                  )
                }}
                  {...defaultCardProps}
                />
            )
        }
        {
          serverError
            ? (
            <Alert variation='warn'>
              <p className="ds-c-alert__text">{t('errors.documentSettings.no-load')}</p>
            </Alert>
              )
            : (
            <>
            {
              flags?.bicOutage202307 && <BicOutageAlert />
            }
            {elmoUnavailable && !flags?.bicOutage202307 && (
              <Alert hideIcon={true} className="my-u-mb--8">
                <p className="ds-c-alert__text">{t('errors.documentSettings.elmoUnavailable')}</p>
              </Alert>
            )}
             <Card
              key="cardEMSN"
              dataTestId="cardEMSN"
              title={t('cards.eMSN')}
              displayValue={eMsnPreferencesDisplayText}
              animationSubject={eMsnPreferences}
              toolTipText={t('toolTips.eMSN')}
              buttonCallbackOrHref={() => openModal('acct-msn-settings')}
              editButtonAriaLabel={t('btns.editEMSNAriaLabel')}
              buttonText={t('btns.edit')}
              disableEdit={flags?.bicOutage202307 || elmoUnavailable}
              buttonSiteActivityProps={{
                core: {
                  component: Component.AccountSettings,
                  elementId: 'accountsettings_emsnedit_click',
                  coreClickData: {
                    linkText: t('btns.edit')
                  }
                }
              }}
              {...defaultCardProps}
            />
            <Card
              key="cardMedicareYouHandbook"
              dataTestId="cardMedicareYouHandbook"
              title={t('cards.medicareYouHandbook')}
              displayValue={eHandbookPreferencesDisplayText}
              animationSubject={eHandbookPreferences}
              buttonCallbackOrHref={() => openModal('acct-handbook-settings')}
              editButtonAriaLabel={t('btns.editMedicareAndYouAriaLabel')}
              buttonText={t('btns.edit')}
              disableEdit={flags?.bicOutage202307 || elmoUnavailable}
              buttonSiteActivityProps={{
                core: {
                  component: Component.AccountSettings,
                  elementId: 'accountsettings_handbookedit_click',
                  coreClickData: {
                    linkText: t('btns.edit')
                  }
                }
              }}
              {...defaultCardProps}
            />
            </>
              )
        }

      </div>
    </div>
  )
}

function BicOutageAlert () {
  const { t } = useTranslation('accountSettings')

  return (
    <Alert variation='warn' className='ds-u-margin-top--3 ds-u-margin-bottom--3'>
      <p className="ds-c-alert__text">{t('bicOutageAlert.content')}</p>
    </Alert>
  )
}
